import { useEffect, useRef } from "react";

/**
 * Hook to handle iOS keyboard appearance/disappearance
 * Prevents the blue bars and dual scrollbar issues on iOS Safari
 */
export function useIOSKeyboard() {
  const initialViewportHeight = useRef<number>(0);
  const isIOS = useRef<boolean>(false);

  useEffect(() => {
    // Detect iOS and mobile devices
    isIOS.current =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    // Only apply on mobile devices (screen width < 768px)
    const isMobile = window.innerWidth < 768;

    if (!isIOS.current || !isMobile) return;

    // Store initial viewport height
    initialViewportHeight.current = window.innerHeight;

    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const currentWidth = window.innerWidth;
      const heightDifference = initialViewportHeight.current - currentHeight;

      // Check if we're still on mobile
      const isStillMobile = currentWidth < 768;

      if (!isStillMobile) {
        // Switched to desktop, clean up mobile styles
        document.body.classList.remove("keyboard-visible");
        const rootElement = document.getElementById("root");
        if (rootElement) {
          rootElement.style.height = "";
        }
        return;
      }

      // If height decreased significantly, keyboard is likely visible
      if (heightDifference > 150) {
        // Keyboard is visible
        document.body.classList.add("keyboard-visible");

        // Adjust root element height
        const rootElement = document.getElementById("root");
        if (rootElement) {
          rootElement.style.height = `${currentHeight}px`;
        }
      } else {
        // Keyboard is hidden
        document.body.classList.remove("keyboard-visible");

        // Restore root element height
        const rootElement = document.getElementById("root");
        if (rootElement) {
          rootElement.style.height = "";
        }
      }
    };

    const handleOrientationChange = () => {
      // Update initial height after orientation change
      setTimeout(() => {
        initialViewportHeight.current = window.innerHeight;
        handleResize();
      }, 100);
    };

    // Handle input focus/blur events
    const handleInputFocus = () => {
      // Small delay to allow keyboard to appear
      setTimeout(handleResize, 300);
    };

    const handleInputBlur = () => {
      // Small delay to allow keyboard to disappear
      setTimeout(handleResize, 300);
    };

    // Add event listeners
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);
    document.addEventListener("focusin", handleInputFocus);
    document.addEventListener("focusout", handleInputBlur);

    // Initial call
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
      document.removeEventListener("focusin", handleInputFocus);
      document.removeEventListener("focusout", handleInputBlur);
      document.body.classList.remove("keyboard-visible");
    };
  }, []);

  return {
    isIOS: isIOS.current,
  };
}
